import format from "date-fns/format";
import { TimeRangeTypes } from "@enums/time-range-types";
import startOfDay from "date-fns/startOfDay";
import endOfDay from "date-fns/endOfDay";
import subDays from "date-fns/subDays";
import subMonths from "date-fns/subMonths";
import subQuarters from "date-fns/subQuarters";
import subYears from "date-fns/subYears";
import startOfMonth from "date-fns/startOfMonth";
import endOfMonth from "date-fns/endOfMonth";
import startOfQuarter from "date-fns/startOfQuarter";
import endOfQuarter from "date-fns/endOfQuarter";
import startOfYear from "date-fns/startOfYear";
import endOfYear from "date-fns/endOfYear";
import subMinutes from "date-fns/subMinutes";
import subHours from "date-fns/subHours";
import { resources } from "@constants/hardcoded-strings";

export const formatDateToString = (date: Date, formatString: string) => {
    return format(date, formatString);
};

export const sortDates = (firstDate: Date, secondDate: Date) => {
    if (firstDate && secondDate) {
        return secondDate.getTime() - firstDate.getTime();
    } else if (firstDate) {
        return -1;
    } else if (secondDate) {
        return 1;
    }
    return 0;
};

export const getTimeRange = (timeRangeType: TimeRangeTypes): { startDate: Date, endDate: Date } => {
    switch (timeRangeType) {
        case TimeRangeTypes.TODAY:
            return {
                startDate: startOfDay(new Date()),
                endDate: endOfDay(new Date())
            };
        case TimeRangeTypes.PAST_MINUTE:
            return {
                startDate: subMinutes(new Date(), 1),
                endDate: new Date()
            };
        case TimeRangeTypes.PAST_THREE_MINUTES:
            return {
                startDate: subMinutes(new Date(), 3),
                endDate: new Date()
            };
        case TimeRangeTypes.PAST_FIVE_MINUTES:
            return {
                startDate: subMinutes(new Date(), 5),
                endDate: new Date()
            };
        case TimeRangeTypes.PAST_TEN_MINUTES:
            return {
                startDate: subMinutes(new Date(), 10),
                endDate: new Date()
            };
        case TimeRangeTypes.PAST_FIFTEEN_MINUTES:
            return {
                startDate: subMinutes(new Date(), 15),
                endDate: new Date()
            };
        case TimeRangeTypes.PAST_THIRTY_MINUTES:
            return {
                startDate: subMinutes(new Date(), 30),
                endDate: new Date()
            };
        case TimeRangeTypes.PAST_ONE_HOUR:
            return {
                startDate: subHours(new Date(), 1),
                endDate: new Date()
            };
        case TimeRangeTypes.PAST_THREE_HOURS:
            return {
                startDate: subHours(new Date(), 3),
                endDate: new Date()
            };
        case TimeRangeTypes.PAST_SIX_HOURS:
            return {
                startDate: subHours(new Date(), 6),
                endDate: new Date()
            };
        case TimeRangeTypes.PAST_TWELVE_HOURS:
            return {
                startDate: subHours(new Date(), 12),
                endDate: new Date()
            };
        case TimeRangeTypes.PAST_TWENTY_FOUR_HOURS:
            return {
                startDate: subHours(new Date(), 24),
                endDate: new Date()
            };
        case TimeRangeTypes.PAST_THREE_DAYS:
            return {
                startDate: subDays(startOfDay(new Date()), 2),
                endDate: endOfDay(new Date())
            };
        case TimeRangeTypes.PAST_SEVEN_DAYS:
            return {
                startDate: subDays(startOfDay(new Date()), 6),
                endDate: endOfDay(new Date())
            };
        case TimeRangeTypes.PAST_FOURTEEN_DAYS:
            return {
                startDate: subDays(startOfDay(new Date()), 13),
                endDate: endOfDay(new Date())
            };
        case TimeRangeTypes.PAST_THIRTY_DAYS:
            return {
                startDate: subDays(startOfDay(new Date()), 30),
                endDate: endOfDay(new Date())
            };
        case TimeRangeTypes.PAST_NINETY_DAYS:
            return {
                startDate: subDays(startOfDay(new Date()), 90),
                endDate: endOfDay(new Date())
            };
        case TimeRangeTypes.CURRENT_MONTH:
            return {
                startDate: startOfMonth(new Date()),
                endDate: endOfDay(new Date())
            };
        case TimeRangeTypes.LAST_MONTH:
            return {
                startDate: startOfMonth(subMonths(new Date(), 1)),
                endDate: endOfMonth(subMonths(new Date(), 1))
            };
        case TimeRangeTypes.CURRENT_QUARTER:
            return {
                startDate: startOfQuarter(new Date()),
                endDate: endOfDay(new Date())
            };
        case TimeRangeTypes.LAST_QUARTER:
            return {
                startDate: startOfQuarter(subQuarters(new Date(), 1)),
                endDate: endOfQuarter(subQuarters(new Date(), 1))
            };
        case TimeRangeTypes.CURRENT_YEAR:
            return {
                startDate: startOfYear(new Date()),
                endDate: endOfDay(new Date())
            };
        case TimeRangeTypes.LAST_YEAR:
            return {
                startDate: startOfYear(subYears(new Date(), 1)),
                endDate: endOfYear(subYears(new Date(), 1))
            };
        default:
            return { startDate: new Date(), endDate: new Date() };
    }
};

export const getTimeRangeLabel = (timeRangeType: TimeRangeTypes) => {
    switch (timeRangeType) {
        case TimeRangeTypes.TODAY:
            return resources.common.timeRanges.today;
        case TimeRangeTypes.PAST_MINUTE:
            return resources.common.timeRanges.pastMinute;
        case TimeRangeTypes.PAST_THREE_MINUTES:
            return resources.common.timeRanges.pastThreeMinutes;
        case TimeRangeTypes.PAST_FIVE_MINUTES:
            return resources.common.timeRanges.pastFiveMinutes;
        case TimeRangeTypes.PAST_TEN_MINUTES:
            return resources.common.timeRanges.pastTenMinutes;
        case TimeRangeTypes.PAST_FIFTEEN_MINUTES:
            return resources.common.timeRanges.pastFifteenMinutes;
        case TimeRangeTypes.PAST_THIRTY_MINUTES:
            return resources.common.timeRanges.pastThirtyMinutes;
        case TimeRangeTypes.PAST_ONE_HOUR:
            return resources.common.timeRanges.pastOneHour;
        case TimeRangeTypes.PAST_THREE_HOURS:
            return resources.common.timeRanges.pastThreeHours;
        case TimeRangeTypes.PAST_SIX_HOURS:
            return resources.common.timeRanges.pastSixHours;
        case TimeRangeTypes.PAST_TWELVE_HOURS:
            return resources.common.timeRanges.pastTwelveHours;
        case TimeRangeTypes.PAST_TWENTY_FOUR_HOURS:
            return resources.common.timeRanges.pastTwentyFourHours;
        case TimeRangeTypes.PAST_THREE_DAYS:
            return resources.common.timeRanges.pastThreeDays;
        case TimeRangeTypes.PAST_SEVEN_DAYS:
            return resources.common.timeRanges.pastSevenDays
        case TimeRangeTypes.PAST_FOURTEEN_DAYS:
            return resources.common.timeRanges.pastFourteenDays;
        case TimeRangeTypes.PAST_THIRTY_DAYS:
            return resources.common.timeRanges.pastThirtyDays;
        case TimeRangeTypes.PAST_NINETY_DAYS:
            return resources.common.timeRanges.pastNinetyDays;
        case TimeRangeTypes.CURRENT_MONTH:
            return resources.common.timeRanges.currentMonth;
        case TimeRangeTypes.LAST_MONTH:
            return resources.common.timeRanges.lastMonth;
        case TimeRangeTypes.CURRENT_QUARTER:
            return resources.common.timeRanges.currentQuarter;
        case TimeRangeTypes.LAST_QUARTER:
            return resources.common.timeRanges.lastQuarter;
        case TimeRangeTypes.CURRENT_YEAR:
            return resources.common.timeRanges.currentYear;
        case TimeRangeTypes.LAST_YEAR:
            return resources.common.timeRanges.lastYear;
        case TimeRangeTypes.CUSTOM:
            return resources.common.timeRanges.custom;
        default:
            return "";
    }
};